'use client';

/**
 * YouTube IFrame API Hook
 *
 * Loads YouTube IFrame API and provides player initialization helpers.
 */

import { useEffect, useState } from 'react';

// YouTube Player States
export const YT_PLAYER_STATES = {
  UNSTARTED: -1,
  ENDED: 0,
  PLAYING: 1,
  PAUSED: 2,
  BUFFERING: 3,
  CUED: 5,
} as const;

export type YouTubePlayerState = (typeof YT_PLAYER_STATES)[keyof typeof YT_PLAYER_STATES];

// Declare YouTube API types globally
declare global {
  namespace YT {
    interface PlayerOptions {
      videoId?: string;
      width?: string | number;
      height?: string | number;
      playerVars?: {
        autoplay?: 0 | 1;
        enablejsapi?: 0 | 1;
        playsinline?: 0 | 1;
        rel?: 0 | 1;
        modestbranding?: 0 | 1;
        [key: string]: unknown;
      };
      events?: {
        onReady?: (event: PlayerEvent) => void;
        onStateChange?: (event: OnStateChangeEvent) => void;
        [key: string]: unknown;
      };
    }

    interface PlayerEvent {
      target: Player;
    }

    interface OnStateChangeEvent extends PlayerEvent {
      data: number;
    }

    class Player {
      constructor(elementId: string | HTMLElement, options: PlayerOptions);
      playVideo(): void;
      pauseVideo(): void;
      stopVideo(): void;
      getDuration(): number;
      getCurrentTime(): number;
      destroy(): void;
    }
  }

  interface Window {
    YT: typeof YT;
    onYouTubeIframeAPIReady: () => void;
  }
}

/**
 * Load YouTube IFrame API script and expose readiness state.
 */
export function useYouTubeAPI(): boolean {
  const [isAPIReady, setIsAPIReady] = useState(false);

  useEffect(() => {
    // Already available
    if (window.YT && window.YT.Player) {
      setIsAPIReady(true);
      return;
    }

    const originalCallback = window.onYouTubeIframeAPIReady;

    window.onYouTubeIframeAPIReady = () => {
      setIsAPIReady(true);
      if (originalCallback) {
        originalCallback();
      }
    };

    // Script already on page
    const existingScript = document.querySelector('script[src*="youtube.com/iframe_api"]');
    if (existingScript) {
      const pollingInterval = setInterval(() => {
        if (window.YT && window.YT.Player) {
          setIsAPIReady(true);
          clearInterval(pollingInterval);
        }
      }, 100);

      return () => {
        clearInterval(pollingInterval);
        window.onYouTubeIframeAPIReady = originalCallback;
      };
    }

    // Inject script
    const script = document.createElement('script');
    script.src = 'https://www.youtube.com/iframe_api';
    script.async = true;
    document.body.appendChild(script);

    return () => {
      window.onYouTubeIframeAPIReady = originalCallback;
    };
  }, []);

  return isAPIReady;
}

/**
 * Extract YouTube video ID from various URL formats.
 */
export function getYouTubeVideoId(url: string): string | null {
  try {
    const urlObj = new URL(url);

    if (urlObj.pathname.includes('/embed/')) {
      return urlObj.pathname.split('/embed/')[1]?.split('?')[0] || null;
    }

    if (urlObj.searchParams.has('v')) {
      return urlObj.searchParams.get('v');
    }

    if (urlObj.hostname.includes('youtu.be')) {
      return urlObj.pathname.substring(1).split('?')[0] || null;
    }

    return null;
  } catch {
    return null;
  }
}
